/**
 * Hospitals domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (with a
 * hand-written detail wrapper where the legacy hook shape relied on a
 * 404-skipping retry predicate).
 *
 * Write hooks go through `useInvalidatingMutation` so cache invalidation
 * is declarative and compile-time-enforced via the `HospitalMutationId`
 * registry in `./invalidation`.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListHospitals,
  listHospitalsQueryKey,
  useGetHospitalsSummary,
  getHospitalsSummaryQueryKey,
  getHospitalQueryKey,
} from '../generated/hooks/hospitalController'

// ── Generated client-function re-exports (imperative consumers) ────

export { listHospitals } from '../generated/clients/listHospitals'
export { getHospitalsSummary } from '../generated/clients/getHospitalsSummary'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  ListHospitalsQueryResponse,
  ListHospitalsQueryParams,
} from '../generated/types/ListHospitals'
export type {
  GetHospitalQueryResponse,
  GetHospitalPathParams,
} from '../generated/types/GetHospital'
export type { GetHospitalsSummaryQueryResponse } from '../generated/types/GetHospitalsSummary'
export type {
  BulkTagsHospitalsMutationRequest,
  BulkTagsHospitalsMutationResponse,
} from '../generated/types/BulkTagsHospitals'
export type {
  BulkDistributorsHospitalsMutationRequest,
  BulkDistributorsHospitalsMutationResponse,
} from '../generated/types/BulkDistributorsHospitals'
export type {
  BulkGposHospitalsMutationRequest,
  BulkGposHospitalsMutationResponse,
} from '../generated/types/BulkGposHospitals'
export type {
  BulkDealsHospitalsMutationRequest,
  BulkDealsHospitalsMutationResponse,
} from '../generated/types/BulkDealsHospitals'
export type {
  AssignHospitalsToCampaignMutationRequest,
  AssignHospitalsToCampaignMutationResponse,
} from '../generated/types/AssignHospitalsToCampaign'
export type {
  ExportHospitalsV2MutationRequest,
  ExportHospitalsV2MutationResponse,
} from '../generated/types/ExportHospitalsV2'

// ── Internal imports for hand-written wrappers + mutations ─────────

import { getHospitalQueryKey as _getHospitalQueryKey } from '../generated/hooks/hospitalController'
import { getHospital } from '../generated/clients/getHospital'
import { bulkTagsHospitals } from '../generated/clients/bulkTagsHospitals'
import { bulkDistributorsHospitals } from '../generated/clients/bulkDistributorsHospitals'
import { bulkGposHospitals } from '../generated/clients/bulkGposHospitals'
import { bulkDealsHospitals } from '../generated/clients/bulkDealsHospitals'
import { assignHospitalsToCampaign } from '../generated/clients/assignHospitalsToCampaign'
import { exportHospitalsV2 } from '../generated/clients/exportHospitalsV2'

import type {
  GetHospitalQueryResponse,
  GetHospital404,
} from '../generated/types/GetHospital'
import type { BulkTagsHospitalsMutationRequest } from '../generated/types/BulkTagsHospitals'
import type { BulkDistributorsHospitalsMutationRequest } from '../generated/types/BulkDistributorsHospitals'
import type { BulkGposHospitalsMutationRequest } from '../generated/types/BulkGposHospitals'
import type { BulkDealsHospitalsMutationRequest } from '../generated/types/BulkDealsHospitals'
import type { AssignHospitalsToCampaignMutationRequest } from '../generated/types/AssignHospitalsToCampaign'
import type { ExportHospitalsV2MutationRequest } from '../generated/types/ExportHospitalsV2'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * Get hospital detail.
 *
 * Hand-written wrapper around the generated `getHospital` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetHospital(
  hospitalId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<GetHospitalQueryResponse, AxiosError<GetHospital404>>,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:hospital_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = hospitalId != null ? String(hospitalId) : undefined
  return useQuery<GetHospitalQueryResponse, AxiosError<GetHospital404>>({
    queryKey:
      id !== undefined
        ? _getHospitalQueryKey({ hospital_id: id })
        : ['hospital', hospitalId],
    queryFn: async ({ signal }) =>
      getHospital({ hospital_id: id as string }, { signal }),
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useBulkTagsHospitals() {
  return useInvalidatingMutation('bulkTagsHospitals', invalidationRegistry, {
    mutationFn: (variables: { data: BulkTagsHospitalsMutationRequest }) =>
      bulkTagsHospitals(variables.data),
  })
}

export function useBulkDistributorsHospitals() {
  return useInvalidatingMutation(
    'bulkDistributorsHospitals',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: BulkDistributorsHospitalsMutationRequest
      }) => bulkDistributorsHospitals(variables.data),
    }
  )
}

export function useBulkGposHospitals() {
  return useInvalidatingMutation('bulkGposHospitals', invalidationRegistry, {
    mutationFn: (variables: { data: BulkGposHospitalsMutationRequest }) =>
      bulkGposHospitals(variables.data),
  })
}

export function useBulkDealsHospitals() {
  return useInvalidatingMutation('bulkDealsHospitals', invalidationRegistry, {
    mutationFn: (variables: { data: BulkDealsHospitalsMutationRequest }) =>
      bulkDealsHospitals(variables.data),
  })
}

export function useAssignHospitalsToCampaign() {
  return useInvalidatingMutation(
    'assignHospitalsToCampaign',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: AssignHospitalsToCampaignMutationRequest
      }) => assignHospitalsToCampaign(variables.data),
    }
  )
}

export function useExportHospitalsV2() {
  return useInvalidatingMutation('exportHospitalsV2', invalidationRegistry, {
    mutationFn: (variables: { data: ExportHospitalsV2MutationRequest }) =>
      exportHospitalsV2(variables.data),
  })
}
